import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { EraserIcon, FileUpIcon, Loader2Icon, PaintbrushIcon } from 'lucide-react';
import { useModelViewport, type ModelAppearance } from '../../hooks/useModelViewport';
import { loadGlbFromSources, parseGlb } from '../../utils/modelLoader';
import { loadCachedModel, saveCachedModel } from '../../utils/modelCache';
import { modelSources } from '../../data/model';
import type { ViewMode } from '../../types/tools';
import type { PrepareRequest, PrepareStep } from '../../types/prepare';
import type { ProtectCommand, ProtectSettings } from '../../types/protect';
import type { RestoreRequest, ViewportHistoryEvent } from '../../types/history';
type Status = {
  kind: 'loading';
  progress: number | null;
} | {
  kind: 'ready';
} | {
  kind: 'empty';
} | {
  kind: 'error';
};
type ModelViewportProps = {
  view: ViewMode;
  resetKey: number;
  appearance: ModelAppearance;
  prepareRequest: PrepareRequest | null;
  onPrepareProgress: (step: PrepareStep) => void;
  onPrepareComplete: (touchpoints: number) => void;
  protectActive: boolean;
  protectSettings: ProtectSettings;
  protectCommand: ProtectCommand | null;
  onProtectDirtyChange: (dirty: boolean) => void;
  onProtectAiDone: (regions: number) => void;
  restoreRequest: RestoreRequest | null;
  onHistoryEvent: (event: ViewportHistoryEvent) => void;
};
export function ModelViewport(props: ModelViewportProps) {
  const brushCursorRef = useRef<HTMLDivElement>(null);
  const {
    containerRef,
    setModel,
    cursor,
    hasDragged
  } = useModelViewport({
    ...props,
    brushCursorRef
  });
  const {
    protectActive,
    protectSettings
  } = props;
  const [status, setStatus] = useState<Status>({
    kind: 'loading',
    progress: null
  });
  const [dragOver, setDragOver] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const [saveNotice, setSaveNotice] = useState<'saved' | 'blocked' | null>(null);
  const noticeTimer = useRef<number>();
  useEffect(() => () => window.clearTimeout(noticeTimer.current), []);
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (modelSources.length > 0) {
        try {
          return await loadGlbFromSources(modelSources, p => !cancelled && setStatus({
            kind: 'loading',
            progress: p
          }));
        } catch {
          // Fall back to a copy saved in this browser if the hosted model is unreachable.
        }
      }
      const cached = await loadCachedModel();
      return cached ? parseGlb(cached.buffer) : null;
    };
    load().then(obj => {
      if (cancelled) return;
      if (!obj) {
        setStatus({
          kind: 'empty'
        });
        return;
      }
      setModel(obj);
      setStatus({
        kind: 'ready'
      });
    }).catch(() => !cancelled && setStatus({
      kind: 'error'
    }));
    return () => {
      cancelled = true;
    };
  }, [setModel]);
  const openFile = async (file: File | undefined) => {
    if (!file) return;
    setStatus({
      kind: 'loading',
      progress: null
    });
    try {
      const buffer = await file.arrayBuffer();
      const obj = await parseGlb(buffer);
      setModel(obj);
      setStatus({
        kind: 'ready'
      });
      const saved = await saveCachedModel(file.name, buffer).catch(() => false);
      setSaveNotice(saved ? 'saved' : 'blocked');
      window.clearTimeout(noticeTimer.current);
      noticeTimer.current = window.setTimeout(() => setSaveNotice(null), saved ? 3500 : 8000);
    } catch {
      setStatus({
        kind: 'error'
      });
    }
  };
  return <div className="absolute inset-0" onDragOver={e => {
    e.preventDefault();
    setDragOver(true);
  }} onDragLeave={() => setDragOver(false)} onDrop={e => {
    e.preventDefault();
    setDragOver(false);
    openFile(e.dataTransfer.files[0]);
  }} data-id="element-880" data-mp="2qjprbsewyr61">
      
      <div ref={containerRef} className="absolute inset-0" style={{
      cursor
    }} aria-label="3D build volume. Drag the model to move it, drag empty space to orbit, scroll to zoom." role="img" data-id="element-881" data-mp="22v6jfdzs0geh" />
      <div ref={brushCursorRef} className="pointer-events-none absolute left-0 top-0 z-10" style={{
      display: 'none'
    }} aria-hidden="true" data-id="element-882" data-mp="3b2ueobkily0x">
        {protectActive && <span className={`absolute bottom-[2px] left-[2px] flex h-[22px] w-[22px] items-center justify-center rounded-full bg-white shadow-[0_1px_4px_rgba(0,0,0,0.25)] ${protectSettings.tool === 'erase' ? 'text-[#e53935]' : 'text-pf-blue'}`} data-id="element-883" data-mp="31dsz2mhvtinh">
          
            {protectSettings.tool === 'erase' ? <EraserIcon className="h-[13px] w-[13px]" strokeWidth={2.2} data-id="element-884" data-mp="2ndi4p9hd5lxk" /> : <PaintbrushIcon className="h-[13px] w-[13px]" strokeWidth={2.2} data-id="element-885" data-mp="2ieoqfeu95qwc" />}
          </span>}
      </div>
      <input ref={fileInput} type="file" accept=".glb,model/gltf-binary" className="sr-only" onChange={e => openFile(e.target.files?.[0])} data-id="element-886" data-mp="2dhf3c5uc5rbo" />

      {dragOver && <div className="pointer-events-none absolute inset-3 rounded-[8px] border-2 border-dashed border-pf-blue bg-pf-blue/5" data-id="element-887" data-mp="bp0cgh9tdcj4" />}

      <AnimatePresence data-id="element-888" data-mp="j78b6757uqqz">
        {status.kind === 'loading' && <motion.div initial={{
        opacity: 0,
        y: -4
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.15,
        ease: [0.23, 1, 0.32, 1]
      }} className="pointer-events-none absolute left-1/2 top-[18px] flex -translate-x-1/2 items-center gap-2 rounded-full bg-white px-3 py-[5px] text-[11.5px] text-pf-ink shadow-[0_1px_4px_rgba(0,0,0,0.12)]" role="status" data-id="element-889" data-mp="o7cr9w27pjgo">
          
            <Loader2Icon className="h-3 w-3 animate-spin text-pf-blue" data-id="element-890" data-mp="59a13dxxhgha" />
            Loading model{status.progress !== null ? ` · ${Math.round(status.progress * 100)}%` : '…'}
          </motion.div>}

        {(status.kind === 'error' || status.kind === 'empty') && <motion.div initial={{
        opacity: 0,
        scale: 0.96
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: [0.23, 1, 0.32, 1]
      }} className="absolute left-1/2 top-1/2 w-[290px] -translate-x-1/2 -translate-y-1/2 rounded-[7px] bg-white p-4 text-center shadow-[0_3px_12px_rgba(20,28,38,0.14)] ring-1 ring-black/[0.06]" role="alert" data-id="element-891" data-mp="1lnjmbz6vip3w">
          
            <p className="text-[13.5px] font-bold text-pf-ink" data-id="element-892" data-mp="3vov0bythfiya">{status.kind === 'error' ? 'Couldn’t read that file' : 'Add a model to the build'}</p>
            <p className="mt-1 text-[11.5px] leading-snug text-pf-muted" data-id="element-893" data-mp="33xyn2x3ifcah">
              {status.kind === 'error' ? 'Make sure it’s a valid .glb file and try again.' : 'Open a .glb file once — it’s saved in this browser and loads automatically from then on.'}
            </p>
            <button type="button" onClick={() => fileInput.current?.click()} className="mx-auto mt-3 flex h-[26px] items-center gap-1.5 rounded-full bg-pf-blue px-4 text-[12px] text-white transition-colors duration-150 hover:bg-pf-blue-hover" data-id="element-894" data-mp="37zk1imvozyms">
            
              <FileUpIcon className="h-3 w-3" data-id="element-895" data-mp="264flajod5g6d" />
              Open .glb File…
            </button>
            <p className="mt-2 text-[10.5px] text-pf-muted" data-id="element-896" data-mp="2aw7dszr2xvz5">or drop it anywhere in the viewport</p>
          </motion.div>}

        {saveNotice && <motion.div key="save-notice" initial={{
        opacity: 0,
        y: -4
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.15,
        ease: [0.23, 1, 0.32, 1]
      }} className={`pointer-events-none absolute left-1/2 top-[18px] max-w-[340px] -translate-x-1/2 rounded-full px-3 py-[5px] text-center text-[11.5px] shadow-[0_1px_4px_rgba(0,0,0,0.12)] ${saveNotice === 'saved' ? 'bg-white text-pf-ink' : 'bg-[#fdecec] text-[#a61b1b]'}`} role="status" data-id="element-897" data-mp="1w8mab4qaluqp">
          
            {saveNotice === 'saved' ? 'Model saved — it will load automatically next time in this browser' : 'This preview blocks browser storage, so the model can’t be saved here'}
          </motion.div>}

        {status.kind === 'ready' && protectActive && <motion.p key="protect-hint" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="pointer-events-none absolute bottom-[16px] left-1/2 -translate-x-1/2 rounded-full bg-pf-nav/85 px-3 py-[5px] text-[11px] text-white" data-id="element-898" data-mp="krbjf231dwu2">
          
            Click and drag on the model to {protectSettings.tool === 'erase' ? 'erase protected areas' : 'paint protected areas'} · Drag background to orbit
          </motion.p>}

        {status.kind === 'ready' && !hasDragged && !protectActive && <motion.p initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="pointer-events-none absolute bottom-[16px] left-1/2 -translate-x-1/2 rounded-full bg-pf-nav/85 px-3 py-[5px] text-[11px] text-white" data-id="element-899" data-mp="uopq49z5r796">
          
            Drag model to move · Drag background to orbit · Right-drag to pan · Scroll to zoom
          </motion.p>}
      </AnimatePresence>
    </div>;
}